import { cn } from "@/lib/cn";
import type { LucideIcon } from "lucide-react";

/**
 * Standardized status pill used across the app.
 *
 * The product surfaces several orthogonal status systems — connection state
 * (connected / not_connected / setup_required / coming_soon), runtime health
 * (healthy / warning / error), issue type (bug / feature / support), and
 * patch lifecycle (draft / review / approved / rejected / changes_requested).
 * They all share the same visual grammar (small rounded pill, tinted bg with
 * matching border, optional leading icon) but were each rebuilt inline.
 *
 * StatusBadge is the single primitive new code should reach for. Existing
 * pages that already pass through `patchStatusMeta` / `actionStatusMeta` etc.
 * keep their inline spans — those meta tables stay the source of truth and
 * can be wired through `className` here when a page wants to converge.
 */
export type StatusTone =
  | "neutral"
  | "brand"
  | "info"
  | "success"
  | "warning"
  | "danger"
  | "muted";

const TONE_CLS: Record<StatusTone, string> = {
  neutral: "text-fg-dim bg-bg-hover border-line",
  brand:   "text-brand-300 bg-brand-500/10 border-brand-500/20",
  info:    "text-status-info bg-status-info/10 border-status-info/20",
  success: "text-status-low bg-status-low/10 border-status-low/20",
  warning: "text-status-medium bg-status-medium/10 border-status-medium/30",
  danger:  "text-status-critical bg-status-critical/10 border-status-critical/20",
  muted:   "text-fg-muted bg-bg-hover border-line-subtle",
};

export function StatusBadge({
  tone = "neutral",
  icon: Icon,
  className = "",
  children,
}: {
  tone?: StatusTone;
  icon?: LucideIcon;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <span
      className={cn(
        // `fc-pill-metrics` pins the line box (min-height + explicit
        // line-height, never wrapping) so a badge is the same height whether
        // it carries an icon, one word or three, and whether its text arrived
        // with the page or a fetch later. Without it the row's height was a
        // function of whatever the status turned out to say.
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium fc-pill-metrics",
        TONE_CLS[tone],
        className,
      )}
    >
      {Icon && <Icon size={11} />}
      {children}
    </span>
  );
}
